// ── 연차 탭 섹션 ──────────────────────────────────────────────────────────────
// 연차 요약 카드 + 연차 내역 카드
// dark mode: CSS 변수 토큰 + Tailwind dark: 클래스
import React from 'react';

export default function LeavesSection({
  // 데이터
  leaveLoading, leaveData, leaveSummaryStats, leaveSummaryPresence, filteredLeaveRows,
  activeLeaveFilters, expandedLeave, leaveFilterDefs,
  // 날짜 포맷 함수 (Dashboard에서 주입)
  fmtDate, weekdayOne,
  // 이벤트 콜백
  onToggleFilter, onToggleDetail, matchesLeaveFilter,
}) {
  return (
    <div className="grid gap-3">

      {/* ── 연차 요약 카드 ── */}
      <section className="bg-white dark:bg-[var(--bg-card)] rounded-xl shadow-card border border-[var(--border)] p-4 w-full min-w-0">
        <h3 className="mt-0 mb-3.5 text-[15px] font-extrabold text-[var(--text)] tracking-tight">
          연차 요약
        </h3>
        {leaveLoading ? (
          <div className="text-[var(--text-muted)] py-3.5 text-[13px] text-center">불러오는 중...</div>
        ) : (
          <div className="grid gap-3">
            {/* 남은 연차 히어로 */}
            <div className="rounded-xl px-5 py-4 border border-blue-200 dark:border-blue-800/50 bg-gradient-to-br from-blue-50 via-blue-50/50 to-slate-50 dark:from-blue-950/40 dark:via-blue-950/20 dark:to-transparent">
              <div className="text-[12px] font-bold text-blue-500 dark:text-blue-400 mb-2 tracking-wider uppercase">
                남은 연차
              </div>
              <div className="text-[36px] leading-none font-black tracking-tight text-blue-800 dark:text-blue-300">
                {Number(leaveData.summary?.remaining || 0)}일
              </div>
            </div>

            {/* 유형별 필터 버튼: 합계가 0이어도 내역이 있으면 표시 */}
            <div
              className="grid gap-2"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))' }}
            >
              {leaveFilterDefs
                .filter(f => Number(leaveSummaryStats[f.key] || 0) > 0 || Boolean(leaveSummaryPresence?.[f.key]))
                .map(filter => {
                  const active = activeLeaveFilters.includes(filter.key);
                  return (
                    <button
                      key={filter.key}
                      type="button"
                      className="rounded-xl px-3 py-2.5 grid gap-0.5 text-left cursor-pointer border transition-colors"
                      // 동적 color 값이라 border/background color는 inline style 유지
                      style={{
                        borderColor: active ? filter.color : undefined,
                        background: active ? `${filter.color}15` : undefined,
                        color: active ? filter.color : undefined,
                      }}
                      onClick={() => onToggleFilter(filter.key)}
                    >
                      <span
                        className="text-[11px] font-bold opacity-75"
                        style={{ color: active ? filter.color : 'var(--text-muted)' }}
                      >
                        {filter.label}
                      </span>
                      <strong
                        className="text-[15px] font-black"
                        style={{ color: active ? filter.color : 'var(--text)' }}
                      >
                        {Number(leaveSummaryStats[filter.key] || 0)}
                      </strong>
                    </button>
                  );
                })}
            </div>
          </div>
        )}
      </section>

      {/* ── 연차 내역 카드 ── */}
      <section className="bg-white dark:bg-[var(--bg-card)] rounded-xl shadow-card border border-[var(--border)] p-4 w-full min-w-0">
        <h3 className="mt-0 mb-3.5 text-[15px] font-extrabold text-[var(--text)] tracking-tight">
          연차 내역
        </h3>
        {leaveLoading ? (
          <div className="text-[var(--text-muted)] py-3.5 text-[13px] text-center">불러오는 중...</div>
        ) : (
          <div className="grid gap-1.5">
            {filteredLeaveRows.length === 0 && (
              <div className="text-[var(--text-muted)] py-3.5 text-[13px] text-center">데이터가 없습니다.</div>
            )}
            {filteredLeaveRows.map(row => {
              const key = String(row.id);
              const opened = Boolean(expandedLeave[key]);
              const isGenerated = matchesLeaveFilter(row, 'generated');
              const isPaidRecognized = matchesLeaveFilter(row, 'paid_recognized');
              const reasonText = String(row.reason || '').trim() || '-';
              const chips = [row.type, row.category, isPaidRecognized ? '유급인정' : '']
                .map(v => String(v || '').trim()).filter(Boolean)
                .filter((v, i, arr) => arr.indexOf(v) === i);

              return (
                <div
                  key={row.id}
                  className={[
                    'border rounded-xl overflow-hidden',
                    // 자동 부여 연차: 파란 테마
                    isGenerated
                      ? 'border-blue-200 dark:border-blue-800/40'
                      : 'border-[var(--border)]',
                    // 열린 상태
                    opened
                      ? isGenerated
                        ? 'bg-blue-50 dark:bg-blue-950/20 shadow-sm'
                        : 'bg-[var(--bg-subtle)] shadow-sm'
                      : isGenerated
                        ? 'bg-[#f8fbff] dark:bg-blue-950/10'
                        : 'bg-white dark:bg-[var(--bg-card)]',
                  ].join(' ')}
                >
                  <button
                    type="button"
                    className="w-full border-0 bg-transparent text-left cursor-pointer px-3 py-2.5"
                    onClick={() => onToggleDetail(key)}
                  >
                    <div className="flex justify-between items-center gap-2 mb-2">
                      <strong className="text-[13px] text-[var(--text)] font-bold">
                        {fmtDate(row.date)} ({weekdayOne(row.date)})
                      </strong>
                      <span className={`text-[12px] font-bold ${opened ? 'text-pine-900 dark:text-accent-400' : 'text-[var(--text-muted)]'}`}>
                        {opened ? '상세 접기' : '상세 보기'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {chips.length > 0
                        ? chips.map(c => (
                          <span key={`${key}-${c}`} className="inline-flex items-center h-[24px] px-2.5 rounded-full border border-[var(--border)] bg-[var(--bg-subtle)] text-[var(--text-sub)] font-semibold text-[12px]">
                            {c}
                          </span>
                        ))
                        : <span className="inline-flex items-center h-[24px] px-2.5 rounded-full border border-[var(--border)] bg-[var(--bg-subtle)] text-[var(--text-sub)] font-semibold text-[12px]">-</span>
                      }
                      <span className="inline-flex items-center h-[24px] px-2.5 rounded-full border border-[var(--border)] bg-[var(--bg-subtle)] text-[var(--text-sub)] font-semibold text-[12px]">
                        {row.count}일
                      </span>
                      <span className="inline-flex items-center h-[24px] px-2.5 rounded-full border border-[var(--border)] bg-[var(--bg-subtle)] text-[var(--text-sub)] font-semibold text-[12px]">
                        남은 연차 {row.remaining_after === null ? '-' : row.remaining_after}
                      </span>
                    </div>
                  </button>

                  {/* 상세 패널 */}
                  {opened && (
                    <div className="border-t border-[var(--border)] px-3 py-2.5 bg-[var(--bg-subtle)]">
                      <div className="text-[13px] text-[var(--text-sub)] overflow-wrap-anywhere break-words">
                        {reasonText}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
